import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/core/api';
import { mintOrgSession } from '@/core/sessionAuth';
import {
  buildChooseOrgPath,
  buildLoginPath,
  isLegacyAppPath,
  isOrganizationsUiPath,
  migrateLegacyPath,
  pickOrgForLegacyRedirect,
} from '@/core/urlRouting';
import type { MeResponse } from '@/types/api';
import { Spinner } from '@/components/primitives/Spinner';

/**
 * Redirect pre-migration flat URLs (``/creatives/:id``, etc.) to ``/org/:orgId/…``.
 */
export function LegacyFlatRedirect() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [target, setTarget] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const returnPath = `${location.pathname}${location.search}${location.hash}`;

  useEffect(() => {
    if (loading || !isAuthenticated) return;
    let cancelled = false;

    async function resolve() {
      try {
        const me = await apiClient<MeResponse>('/users/me');
        if (cancelled) return;

        const pathname = location.pathname;
        if (!isLegacyAppPath(pathname) && !isOrganizationsUiPath(pathname)) {
          setTarget(returnPath);
          return;
        }

        const orgId = pickOrgForLegacyRedirect(me);
        if (orgId == null) {
          setTarget(buildChooseOrgPath(returnPath));
          return;
        }

        const dest = migrateLegacyPath(returnPath, orgId);
        const orgMatch = dest.match(/^\/org\/(\d+)/);
        if (orgMatch) {
          await mintOrgSession(orgMatch[1]);
        }
        if (!cancelled) setTarget(dest);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to resolve workspace');
        }
      }
    }

    void resolve();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, loading, location.pathname, returnPath]);

  if (loading) {
    return (
      <div className="aspectly:flex aspectly:h-screen aspectly:items-center aspectly:justify-center aspectly:bg-surface">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={buildLoginPath(returnPath)} replace />;
  }

  if (error) {
    return (
      <div className="aspectly:flex aspectly:h-screen aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:gap-4 aspectly:bg-surface aspectly:px-6">
        <p className="aspectly:text-on-surface-variant aspectly:text-sm">{error}</p>
      </div>
    );
  }

  if (target) {
    return <Navigate to={target} replace />;
  }

  return (
    <div className="aspectly:flex aspectly:h-screen aspectly:items-center aspectly:justify-center aspectly:bg-surface">
      <Spinner size="lg" />
    </div>
  );
}
